import type { ControlBridgeQueueItem, ControlBridgeViewModel } from './deriveViewModel';
import type { BridgeSessionAssessmentModel } from './missionControl/bridgeSessionAssessment';
import type { KryspaceControlBridgeStructure } from '../../types/kryspaceControlBridgeStructure';
import type { ScopeContext } from './filters';
import { queueItemMatchesStructureScopeId } from '../../utils/scopeIdPlane';
import {
  deriveDriveSubdomainRows,
  type SubdomainFlowRow,
} from './deriveSubdomainFlow';
import {
  efficiencyPhrase,
  entropyFromCorrectionFactor,
  entropyStateLabel,
  healthMeaningFromEntropy,
  plainHealthLabel,
} from './entropyLanguage';

export interface VgrComponentMetric {
  key: string;
  label: string;
  actual: number | null;
  ideal: number | null;
  displayActual: string;
  displayIdeal: string;
}

export interface DiagnoseScopeHealthRow extends SubdomainFlowRow {
  entropy: number | null;
  healthLabel: string;
  healthMeaning: 'good' | 'warn' | 'alert' | 'muted';
  correctionFactor: number | null;
  vgrSummary: string;
  healthSource: 'manifest' | 'proxy';
  vgrComponents: VgrComponentMetric[];
}


function formatRatio(value: number | null, asPercent = true): string {
  if (value == null || Number.isNaN(value)) return 'Not assessed';
  if (asPercent) return `${Math.round(Math.max(0, Math.min(100, value * 100)))}%`;
  return value.toFixed(2);
}

/** Proxy VGR chain until kryspace emits per-scope artifact_item properties. */
export function proxyVgrComponents(
  correctionFactor: number | null,
  openIssues: number,
  activeWork: number
): VgrComponentMetric[] {
  const cf = correctionFactor ?? 0.55;
  const costLoad = Math.min(1, openIssues * 0.12 + activeWork * 0.06);
  const idealCost = 0.35;

  return [
    {
      key: 'correction_factor',
      label: 'Operating efficiency',
      actual: cf,
      ideal: 1,
      displayActual: formatRatio(cf),
      displayIdeal: '100%',
    },
    {
      key: 'value_generated',
      label: 'Value generated',
      actual: cf,
      ideal: 1,
      displayActual: formatRatio(cf),
      displayIdeal: '100%',
    },
    {
      key: 'cost_to_generate',
      label: 'Cost to generate',
      actual: costLoad,
      ideal: idealCost,
      displayActual: formatRatio(costLoad),
      displayIdeal: formatRatio(idealCost),
    },
  ];
}

function manifestScopeHealth(
  scopeId: string,
  vm: ControlBridgeViewModel
): {
  correctionFactor: number | null;
  entropy: number | null;
  components: VgrComponentMetric[];
} | null {
  const row = vm.scopeHealthQueue.find((item) =>
    queueItemMatchesStructureScopeId(item, scopeId)
  );
  if (!row) return null;

  const cf =
    typeof row.correctionFactor === 'number'
      ? row.correctionFactor
      : typeof row.vgrCorrectionFactor === 'number'
        ? row.vgrCorrectionFactor
        : null;

  const rawEntropy = typeof row.entropy === 'number' ? row.entropy : null;
  const entropy =
    cf != null
      ? entropyFromCorrectionFactor(cf)
      : entropyStateLabel(rawEntropy) != null
        ? rawEntropy
        : null;

  const components: VgrComponentMetric[] = [];

  if (row.vgrComponents?.length) {
    for (const comp of row.vgrComponents) {
      components.push({
        key: comp.key,
        label: comp.label,
        actual: comp.actual ?? null,
        ideal: comp.ideal ?? null,
        displayActual: comp.displayActual ?? formatRatio(comp.actual ?? null),
        displayIdeal: comp.displayIdeal ?? formatRatio(comp.ideal ?? null),
      });
    }
  } else if (cf != null) {
    const valueActual = row.valueGeneratedActual ?? cf;
    const valueIdeal = row.valueGeneratedIdeal ?? 1;
    const costActual = row.costToGenerateActual ?? null;
    const costIdeal = row.costToGenerateIdeal ?? 0.35;
    components.push(
      {
        key: 'value_generated',
        label: 'Value generated',
        actual: valueActual,
        ideal: valueIdeal,
        displayActual: formatRatio(valueActual),
        displayIdeal: formatRatio(valueIdeal),
      },
      {
        key: 'cost_to_generate',
        label: 'Cost to generate',
        actual: costActual,
        ideal: costIdeal,
        displayActual: formatRatio(costActual),
        displayIdeal: formatRatio(costIdeal),
      },
      {
        key: 'correction_factor',
        label: 'Operating efficiency',
        actual: cf,
        ideal: 1,
        displayActual: formatRatio(cf),
        displayIdeal: '100%',
      }
    );
  }

  const resolvedComponents =
    components.length > 0
      ? components
      : cf != null
        ? proxyVgrComponents(cf, row.openIssues ?? 0, row.activeActivities ?? 0)
        : [];

  if (resolvedComponents.length === 0 && cf == null && entropy == null) {
    return null;
  }

  return {
    correctionFactor: cf,
    entropy,
    components: resolvedComponents,
  };
}

function workshopEntropyForRow(
  label: string,
  assessment?: BridgeSessionAssessmentModel
): number | null {
  if (!assessment?.domainEntropy.length) return null;
  const normalized = label.trim().toLowerCase();
  const hit = assessment.domainEntropy.find(
    (row) => row.scope.trim().toLowerCase() === normalized
  );
  return hit?.entropy ?? null;
}

export function deriveDiagnoseScopeHealthRows(
  structure: KryspaceControlBridgeStructure,
  scope: string,
  vm: ControlBridgeViewModel,
  openIssues: ControlBridgeQueueItem[],
  eligibleWork: ControlBridgeQueueItem[],
  scopeContext?: ScopeContext
): DiagnoseScopeHealthRow[] {
  const baseRows = deriveDriveSubdomainRows(structure, scope, openIssues, eligibleWork, scopeContext);
  const assessment = vm.bridgeSessionAssessment;

  return baseRows.map((row) => {
    const fromManifest = manifestScopeHealth(row.scopeId, vm);
    const workshopEntropy = workshopEntropyForRow(row.label, assessment);
    const correctionFactor = fromManifest?.correctionFactor ?? null;
    const manifestEntropy = fromManifest?.entropy ?? null;
    const labeledEntropy =
      entropyStateLabel(manifestEntropy) != null
        ? manifestEntropy
        : entropyStateLabel(workshopEntropy) != null
          ? workshopEntropy
          : null;
    const entropy =
      correctionFactor != null ? entropyFromCorrectionFactor(correctionFactor) : labeledEntropy;
    const vgrComponents =
      fromManifest?.components ??
      (correctionFactor != null
        ? proxyVgrComponents(correctionFactor, row.openIssues, row.activeActivities)
        : []);

    return {
      ...row,
      entropy,
      healthLabel: entropyStateLabel(entropy) ?? plainHealthLabel(entropy),
      healthMeaning: entropyStateLabel(entropy) != null ? 'muted' : healthMeaningFromEntropy(entropy),
      correctionFactor,
      vgrSummary: efficiencyPhrase(correctionFactor),
      healthSource: fromManifest != null ? 'manifest' : 'proxy',
      vgrComponents,
    };
  });
}

export { DiagnoseHealthGrid } from './DiagnoseHealthGrid';
